import dicomImageLoader from '@cornerstonejs/dicom-image-loader';
import { api } from 'dicomweb-client';
import { DICOMWeb } from '@ohif/core';
import type { StudyDicomInstance } from './types';

const YIELD_EVERY_N_INSTANCES = 4;

function getImageLoaderType(imageId: string): string {
  const match = /^(\w+):/.exec(imageId);
  return match?.[1] ?? '';
}

function buildWadoUriUrl(instance: StudyDicomInstance): string | null {
  const { StudyInstanceUID, SeriesInstanceUID, SOPInstanceUID, wadoUri } = instance;

  if (!wadoUri || !StudyInstanceUID || !SeriesInstanceUID || !SOPInstanceUID) {
    return null;
  }

  const params = [
    'requestType=WADO',
    `studyUID=${StudyInstanceUID}`,
    `seriesUID=${SeriesInstanceUID}`,
    `objectUID=${SOPInstanceUID}`,
    'contentType=application/dicom',
    'transferSyntax=*',
  ];

  return `${wadoUri}?${params.join('&')}`;
}

function stripFrameFromDicomWebUrl(url: string): string {
  return url.replace(/&frame=\d+.*$/, '');
}

async function fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
  const headers = DICOMWeb.getAuthorizationHeader();
  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Failed to retrieve DICOM (${response.status})`);
  }

  return response.arrayBuffer();
}

async function retrieveWadoRsInstance(instance: StudyDicomInstance): Promise<ArrayBuffer> {
  const { wadoRoot, StudyInstanceUID, SeriesInstanceUID, SOPInstanceUID } = instance;

  if (!wadoRoot || !StudyInstanceUID || !SeriesInstanceUID || !SOPInstanceUID) {
    throw new Error('Missing WADO-RS identifiers');
  }

  const headers = DICOMWeb.getAuthorizationHeader();
  const client = new api.DICOMwebClient({
    url: wadoRoot,
    headers,
  });

  return client.retrieveInstance({
    studyInstanceUID: StudyInstanceUID,
    seriesInstanceUID: SeriesInstanceUID,
    sopInstanceUID: SOPInstanceUID,
  });
}

export async function fetchInstanceDicomBytes(instance: StudyDicomInstance): Promise<ArrayBuffer> {
  if (instance.url) {
    return dicomImageLoader.wadouri.loadFileRequest(instance.url);
  }

  const imageId = instance.imageId;

  if (imageId) {
    const loaderType = getImageLoaderType(imageId);

    if (loaderType === 'dicomweb') {
      const url = stripFrameFromDicomWebUrl(imageId.slice('dicomweb:'.length));
      return fetchArrayBuffer(url);
    }

    if (loaderType === 'wadors') {
      return retrieveWadoRsInstance(instance);
    }

    if (loaderType === 'wadouri' || loaderType === 'dicomfile') {
      const fileKey = imageId.includes(':') ? imageId.slice(imageId.indexOf(':') + 1) : imageId;
      return dicomImageLoader.wadouri.loadFileRequest(fileKey);
    }
  }

  if (instance.wadoRoot) {
    return retrieveWadoRsInstance(instance);
  }

  const wadoUriUrl = buildWadoUriUrl(instance);
  if (wadoUriUrl) {
    return fetchArrayBuffer(wadoUriUrl);
  }

  throw new Error('No DICOM retrieval path for instance');
}

export async function yieldToMainThread(): Promise<void> {
  await new Promise<void>(resolve => {
    window.setTimeout(resolve, 0);
  });
}

export { YIELD_EVERY_N_INSTANCES };
